<template>
  <div class="fileUploaderByYear" :key="filesKey">
    <strong>{{ title }}</strong>

    <div v-for="yearOption in getYearOptions" :key="`${filesPerYear[yearOption].key}`">
      <div class="fileUploaderByYear__row" v-for="(file, index) in filesPerYear[yearOption].files" :key="file.key">
        <div class="fileUploaderByYear__row--year">
          <label>{{ $t("global.year") }}</label>
          <input
            type="text"
            disabled
            :value="yearOption"
          />
        </div>

        <div class="fileUploaderByYear__row--semester" v-if="semestersRequired">
          <label>{{ $t('integrityCompliance.integrity.semester') }}</label>
          <input
            type="text"
            v-model="file.semester"
            disabled
          />
        </div>

        <div class="fileUploaderByYear__filename" v-if="filenameRequired">
          <label class="text-black">{{ filenameLabel || $t('global.filename')}}</label>
          <input
            type="text"
            v-model="file.name"
            @change="alertFilesChanged"
          />
          <div class="ui-input-error-msg">
            <span v-if="file.errored && showErrors">{{ $t("global.inputs.required") }}</span>
          </div>
        </div>

        <div class="fileUploaderByYear__file">
          <label>{{ $t("global.file") }}</label>
          <FileUploader
              :key="`${file.key}_fileUploader`"
              :id="`${file.key}_fileUploader`"
              :placeholder="file.placeholder"
              type=".pdf"
              validate="pdf"
              :label="$t('global.browse')"
              :fileUrl="file.file_url"
              @setFile="setFile($event, yearOption, index)"
          />
        </div>
      </div>

    </div>

  </div>

</template>

<script>
const FileUploader = () => import("@/components/FileUploader");
import { yearOptions, maxSemesters } from "@/lib/constants.js";

import API_SERVICE from "@/lib/api/IntegrityCompliance.js";
const api = new API_SERVICE();

export default {
  inject: ['IC_getMainCategory'], // data from main category
  props: {
    title: {
      type: String,
      default: ''
    },
    semestersRequired: {
      type: Boolean,
      default: false
    },
    numberOfRequiredSemesters: {
      type: Number,
      default: 2
    },
    filenameRequired: {
      type: Boolean,
      default: false
    },
    filenameLabel: {
      type: String,
      default: ''
    },
    subcategory: {
      type: Object,
      default: () => {
        return {
          id: 0, // number
          name: '',
          title: '',
          description: { ops: []},
          is_parent: false // boolean
        }
      }
    },
    allowedNumberOfFiles: {
      type: Number,
      default: 1
    },
    showErrors: {
      type: Boolean,
      default: false
    }
  },
  components: {
    FileUploader
  },
  computed: {
    yearOptions () {
      return yearOptions()
    },
    getYearOptions () {
      return Object.keys(this.filesPerYear);
    },
    getSemesters () {
      if (!this.semestersRequired) {
        return 1
      } else {
        return this.numberOfRequiredSemesters > maxSemesters
          ? maxSemesters
          : this.numberOfRequiredSemesters;
      }
    }
  },
  data: function() {
    return {
      categoryData: {},
      filesKey: `filesPerYears_${new Date().getTime()}`,
      isLoading: false,
      filesPerYear: {}
    }
  },
  created () {
    this.setDefaultFilesConfiguration()
  },
  async mounted () {
    this.categoryData = this.IC_getMainCategory();
    if (this.subcategory.id) {
      const subcategoryId = this.subcategory.id;
      try {
        await this.getDocumentsBySubcategoryAndYear(subcategoryId, this.yearOptions);
        this.filesKey = `filesPerYear_${new Date().getTime()}`;
      } catch (error) {
        console.error(error);
      }
    }
  },
  methods: {
    setDefaultFilesConfiguration () {
      for (const year of this.yearOptions) {
        this.filesPerYear[year] = {
          files: [],
          key: `filesPerYear_${year}_${new Date().getTime()}`
        };
        for (let index = 0; index < this.allowedNumberOfFiles; index++) {
          for (let subIndex = 0; subIndex < this.getSemesters; subIndex++) {
            this.filesPerYear[year].files.push({
              document_id: null,
              name: '',
              path: '',
              file_url: '',
              year,
              file: null,
              key: `file_${index}_${year}_${subIndex}_${new Date().getTime()}`,
              semester: this.semestersRequired ? subIndex+1 : null,
              placeholder: this.$t('global.loading')
            })
          }
        }
      }
    },
    async getDocumentsBySubcategoryAndYear (subcategoryId, years) {
      try {
        const filesPerYear = {};
        for (const year of years) {
          filesPerYear[year] = {
            files: [],
            key: `filesPerYear_${year}_${new Date().getTime()}`
          };
          let savedFiles = []
          for (let index = 1; index <= this.getSemesters; index++) {
            const semester = this.semestersRequired ? index : null
            const fileTemplate = {
              document_id: null,
              name: '',
              path: '',
              file_url: '',
              year,
              file: null,
              key: `file_${year}_${semester}_${new Date().getTime()}`,
              semester,
              placeholder: this.$t('global.selectFile')
            };

            const documents = await api.getDocumentsBySubcategoryAndYear(subcategoryId, year, semester);
            if (documents && documents.length) {
              const formattedFiles = documents.map(document => (
                {
                  ...fileTemplate,
                  ...document,
                  key: `file_${document.document_id}_${year}_${index}_${new Date().getTime()}`
                }
              )).slice(0, this.allowedNumberOfFiles);
              savedFiles = savedFiles.concat(formattedFiles)
            }
          }
          
          const existingFilesTemplate = [...this.filesPerYear[year].files].map(document => ({ ...document, placeholder: this.$t('global.selectFile') })); // in case API return less files than expected, or empty array
          if (savedFiles.length < existingFilesTemplate.length) { // need to populate remaining empty files
            if (this.semestersRequired) {
              const filledFiles = existingFilesTemplate.map(document => {
                const existingFile = savedFiles.find(updatedFile => // match semesters in order to display semester 1 and 2 respectively in order
                  document.semester === updatedFile.semester &&
                  document.year === updatedFile.year
                )
                return existingFile || document
              })
              savedFiles = [...filledFiles]
            } else {
              savedFiles = savedFiles.concat(existingFilesTemplate.slice(savedFiles.length, existingFilesTemplate.length))
            }
          }
          filesPerYear[year].files = savedFiles;
        }
        this.filesPerYear = {...filesPerYear}
      } catch (error) {
        throw error;
      }
    },
    setFile(newFile, year, index) {
      const timestamp = Date.now();
      let filesPerYear = [...this.filesPerYear[year].files];

      const yearFile = filesPerYear[index];
      const semester = this.semestersRequired ? yearFile.semester : '';
      const name = `${timestamp}.pdf`;
      const base = `IC/${this.categoryData.name}/${this.subcategory.name}/${year}`;
      const path = this.semestersRequired
        ? `${base}/${semester}/${name}`
        : `${base}/${name}`
      const filename = this.filenameRequired ? yearFile.name : path;

      const errored = this.filenameRequired
        ?  !yearFile.name.trim()
        : false
      filesPerYear[index] = {
        ...yearFile,
        name: filename,
        path,
        file: newFile,
        errored 
      }

      if(!newFile) {
        filesPerYear[index].key = `file_${year}_${index}_${new Date().getTime()}`
      }

      this.filesPerYear[year].files = [...filesPerYear]
      this.alertFilesChanged();
    },
    alertFilesChanged () {
      const subcategoryFiles = this.getFullFilesArray()

      this.$emit('updatedFiles', {
        subcategoryId: this.subcategory.id,
        subcategoryName: this.subcategory.name,
        subcategoryFiles
      })
    },
    getFullFilesArray () {
      let subcategoryFiles = []
      this.getYearOptions.forEach(year => {
        subcategoryFiles = subcategoryFiles.concat(this.filesPerYear[year].files)
      });
      return subcategoryFiles;
    }
  }
}
</script>

<style lang="scss" scoped>

.fileUploaderByYear {
  &__row {
    display: flex;
    margin-top: $global_padding;
    gap: $global_padding;
    align-items: baseline;
    &--year {
      display: flex;
      flex-direction: column;
      max-width: 50px;
    }
    &--semester {
      display: flex;
      flex-direction: column;
      justify-content: center;
      max-width: min-content
    }
  }
}

</style>
